/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { FC, lazy, useCallback, useEffect, useMemo, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import dayjs from "dayjs"
import { Link, useLocation, useHistory } from "react-router-dom"
import { Table, Icon, Radio } from "grab-ui"
import { Row, Col } from "antd"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { MenuGroupCard } from "components/cards"
import { useTranslationsMap } from "common/utils/i18n"
import {
  WithMenuHeader,
  WithMenuHeaderInjectedProps,
} from "components/hoc/WithMenuHeader"
import {
  CATALOGUES,
  CREATE_CATALOGUE,
  CREATE_MENU,
  MENUS,
} from "constants/routes"
import { MultiStoreMenuAnnouncementBanner } from "components/banners"
import { storageHelper } from "utils/storage"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { Config } from "constants/config"
import LazyLoadWrapper from "components/LazyLoadWrapper"
import { Button, Flex, Tag, Typography } from "@duxton-web/ui"
import { AddCircleFilled } from "@duxton-web/icons"
import { getMenuEditorDeepLinkPath } from "utils/menu_helper"
import { SearchBar } from "./SearchBar"
import { menuGroupListActionContainer, menuGroupListContainer } from "./style"
import { useIsMartMenuGroup } from "./useIsMartMenuGroup"

const ItemPhotoCoverageBanner = lazy(() =>
  import("components/banners/ItemPhotoCoverageBanner"),
)

interface StateType {
  menuGroupName?: string // publishing menu data - will update on integration
}

interface TableDataType {
  key: string
  name: string
  lastUpdated: string
  status: string
  itemsWithoutPhotosCount: number
}

export const MenuGroupList: FC = () => {
  const history = useHistory()
  const location = useLocation<StateType>()
  const dispatch = useDispatch()

  const { merchantGroupID, menuGroups } = useSelector(
    (state: AppState) => state.menu,
    shallowEqual,
  )

  const itemsWithoutPhotoCount: number = useMemo(() => {
    return menuGroups.reduce((acc, menuGroup) => {
      return acc + (menuGroup.itemsWithoutPhotosCount || 0)
    }, 0)
  }, [menuGroups])

  const bannerEmptyImage = require("../../assets/images/menu/banner_empty_add_photo.svg")

  const menuGroupsData: TableDataType[] = menuGroups.map(menuGroup => ({
    key: menuGroup.menuGroupID,
    name: menuGroup.name,
    lastUpdated: dayjs(menuGroup.updatedAt).format("DD MMM YYYY"),
    status: menuGroup.status,
    itemsWithoutPhotosCount: menuGroup.itemsWithoutPhotosCount || 0,
  }))

  const [showBanner, setShowBanner] = useState(false)

  const [viewMode, setViewMode] = useState<"card" | "list">("card")

  const fetchMenuGroupList = async () => {
    const t0 = performance.now()
    const resp = await dispatch(
      actionCreator.menu.fetchMenuGroupList({
        merchantGroupID,
        isWithItemPhotoCount: Config.enableItemPhotoCoverage,
      }),
    )
    const t1 = performance.now()
    let labels = {
      MERCHANT_GROUP_ID: merchantGroupID,
      LOAD_TIME: t1 - t0,
      LOADING_STATUS: "SUCCESS",
      REASON: "",
      ERROR_MSG: "",
    }
    if (resp?.type?.endsWith("_FAIL")) {
      labels = {
        ...labels,
        LOADING_STATUS: "FAILURE",
        ...getResponseErrorEventLabels(resp),
      }
    }
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupHomePage,
      action: EventAction.pageView,
      labels,
    })
  }

  const handleMenuGroupBanner = () => {
    const showMenuGroupBanner = storageHelper.getShowMenuGroupBanner()
    if (showMenuGroupBanner === null) {
      storageHelper.setShowMenuGroupBanner(true)
      setShowBanner(true)
    } else {
      setShowBanner(showMenuGroupBanner)
    }
  }

  const onMount = () => {
    fetchMenuGroupList()
    handleMenuGroupBanner()
  }

  useEffect(onMount, [])

  const isMartMenuGroup = useIsMartMenuGroup()
  const textMap = useTranslationsMap({
    title: "menu.menuGroups.title",
    createNewMenu: `menu.menuGroup.create.${
      isMartMenuGroup ? "forCatalogue" : "forMenu"
    }.createNewMenuBtn`,
    searchByMenuName: "menu.menuGroups.placeholder.searchMenu",
    failPublishMenuTitle: "menu.menuGroups.failPublishMenu.title",
    failPublishMenuDescription: "menu.menuGroups.failPublishMenu.description",
    failPublishMenuOk: "menu.menuGroups.failPublishMenu.ok",
    failPublishMenuCancel: "menu.menuGroups.failPublishMenu.cancel",
    itemPhotoCoverageBannerTitle: "itemPhotoCoverage.banner.title",
    itemPhotoCoverageBannerContent: "itemPhotoCoverage.banner.content",
    photosMissingWithCount: "label.photoMissing.withCount",
  })

  const columns = useMemo(() => {
    return [
      {
        title: "Menu Name",
        dataIndex: "name",
        key: "name",
        render: (_: any, record: TableDataType) => {
          return Config.enableItemPhotoCoverage ? (
            <Flex align="center">
              <Typography.Text style={{ width: "300px" }}>
                {record.name}
              </Typography.Text>
              {record.itemsWithoutPhotosCount !== 0 && (
                <Tag color="warning">
                  {textMap.photosMissingWithCount.replace(
                    "{x}",
                    record.itemsWithoutPhotosCount?.toString() || "",
                  )}
                </Tag>
              )}
            </Flex>
          ) : (
            record.name
          )
        },
      },
      {
        title: "Last Updated",
        dataIndex: "lastUpdated",
        key: "lastUpdated",
      },
    ]
  }, [textMap.photosMissingWithCount])

  useEffect(() => {
    const currPathName = location.pathname
    const newPathName = isMartMenuGroup ? CATALOGUES : MENUS
    if (currPathName !== newPathName) {
      history.replace({
        pathname: newPathName,
        search: location.search,
      })
    }
  }, [isMartMenuGroup, history, location.pathname, location.search])

  const handleViewMenuGroupDetailClick = useCallback(
    (menuGroupID: string, itemsWithoutPhotosCount: number = 0) => {
      dispatch(actionCreator.menu.toggleItemPhotoCoverageBanner(false))
      history.push(
        getMenuEditorDeepLinkPath({
          basePath: `${isMartMenuGroup ? CATALOGUES : MENUS}/${menuGroupID}`,
          search: location.search,
        }),
      )
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupHomePage,
        action: EventAction.clickMenuGroup,
        labels: {
          MENU_GROUP_ID: menuGroupID,
          MISSING_PHOTO: itemsWithoutPhotosCount,
        },
      })
    },
    [dispatch, history, isMartMenuGroup, location.search],
  )

  const handleSearch = useCallback(
    val => {
      dispatch(
        actionCreator.menu.fetchMenuGroupList({
          merchantGroupID,
          menuGroupName: val,
        }),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [merchantGroupID],
  )

  const trackBannerView = useCallback(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupHomePage,
      action: EventAction.default,
      labels: {
        AR_LOCATION_STATENAME: EventCategory.menuGroupHomePage,
        NUDGE_ID: "MISSING_PHOTO_NUDGE",
        VIEW_ID: "MISSING_PHOTO_NUDGE",
        PRIORITY_IN_GROUP: "",
        SUMMARY_ORDER: "",
        TITLE_TRANSLATION_KEY:
          "mex-insights-menu-card-heading-items-with-no-photo",
        METRIC: "photo_coverage",
      },
    })
  }, [])

  return (
    <div css={menuGroupListContainer}>
      {showBanner && <MultiStoreMenuAnnouncementBanner />}
      {Config.enableItemPhotoCoverage && itemsWithoutPhotoCount > 0 && (
        <div
          css={css`
            margin-bottom: 24px;
          `}
        >
          <LazyLoadWrapper>
            <ItemPhotoCoverageBanner
              image={bannerEmptyImage}
              title={textMap.itemPhotoCoverageBannerTitle}
              content={textMap.itemPhotoCoverageBannerContent}
              trackPageView={trackBannerView}
            />
          </LazyLoadWrapper>
        </div>
      )}

      <Row type="flex" gutter={32} css={menuGroupListActionContainer}>
        <Col span={16}>
          <Row type="flex" justify="space-between" gutter={32}>
            <Col>
              <Link to={isMartMenuGroup ? CREATE_CATALOGUE : CREATE_MENU}>
                <Button
                  data-testid="action-button-link"
                  onClick={() => {
                    TroyWebTracker.sendEvent({
                      category: EventCategory.menuGroupHomePage,
                      action: EventAction.clickCreateNewMenu,
                      labels: {
                        MERCHANT_GROUP_ID: merchantGroupID,
                      },
                    })
                  }}
                >
                  {textMap.createNewMenu}
                  <AddCircleFilled />
                </Button>
              </Link>
            </Col>
            <Col>
              <Radio.Group
                data-testid="view-mode-radio-group"
                value={viewMode}
                buttonStyle="solid"
                className="blue-radio"
                onChange={e => setViewMode(e.target.value)}
              >
                <Radio.Button value="card">
                  <Icon type="table" />
                </Radio.Button>
                <Radio.Button value="list">
                  <Icon type="bars" />
                </Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
        </Col>
        <Col span={8}>
          <SearchBar
            data-testid="search-bar-link"
            placeholder={textMap.searchByMenuName}
            onSearch={handleSearch}
            onClick={() => {
              TroyWebTracker.sendEvent({
                category: EventCategory.menuGroupHomePage,
                action: EventAction.clickMenuGroupSearch,
                labels: {
                  MERCHANT_GROUP_ID: merchantGroupID,
                },
              })
            }}
          />
        </Col>
      </Row>
      {viewMode === "card" ? (
        <Row type="flex" gutter={[32, 32]}>
          {menuGroups.map(menuGroup => (
            <Col key={menuGroup.menuGroupID} xs={24} md={12} xl={8}>
              <MenuGroupCard
                data-testid="menu-group-card"
                menuGroup={menuGroup}
                onCardClick={item =>
                  handleViewMenuGroupDetailClick(
                    item.menuGroupID,
                    item.itemsWithoutPhotosCount,
                  )
                }
              />
            </Col>
          ))}
        </Row>
      ) : (
        <Table
          data-testid="menu-group-table"
          columns={columns}
          dataSource={menuGroupsData}
          pagination={false}
          onRowClick={menuGroup =>
            handleViewMenuGroupDetailClick(
              menuGroup?.key,
              menuGroup?.itemsWithoutPhotosCount,
            )
          }
        />
      )}
    </div>
  )
}

export const MenuGroupListWithHeaderWrapped = ({
  setHeaderTitle,
}: WithMenuHeaderInjectedProps) => {
  const textMap = useTranslationsMap({
    pageTitle: "menu.title",
  })
  const updateHeader = () => setHeaderTitle(textMap.pageTitle)
  useEffect(updateHeader, [])

  return <MenuGroupList />
}

export const MenuGroupListWithHeader = WithMenuHeader()(
  MenuGroupListWithHeaderWrapped,
)
